import React, { useEffect, useRef, useState } from 'react'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import Col from 'react-bootstrap/Col'
import Row from 'react-bootstrap/Row'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Dropdown from 'react-bootstrap/Dropdown'
import { uniqueId } from 'lodash'
import TriggerDescription from '../TriggerDescription'
import { toast } from 'react-toastify'
import { getValidPreviousIntentId } from '../../../../utils/functions/getValidPreviousIntentId'
import { updateWhatsAppIntent } from '../../../../utils/functions/updateWhatsAppIntent'
import WhatsappWebhookComponent from './WhatsappWebhookComponent'
import useWebhookState from '../../../../utils/hooks/useWebhookState'
import {
  initializeTags,
  addTagToSession,
  removeTagFromSession,
  isTagAvailable
} from '../../../../utils/functions/tagUtils'

interface IProps {
  intent: any
  inputJson: any[]
  setInputJson: React.Dispatch<React.SetStateAction<any[]>>
  closeModal: () => void
  data: any
}

const ButtonGroup: React.FC<IProps> = ({ intent, inputJson, setInputJson, closeModal, data }) => {
  const intentIndex = inputJson.findIndex((item) => item.id === intent.id)
  const allIntentIdsArray = inputJson.map((intentId) => intentId.id)
  const btnMenuDropdownRef = useRef<HTMLDivElement>(null)
  const { isWebhookChecked, handleWebhookCheckboxChange, handleFulfillmentChange } =
    useWebhookState(intent, intentIndex, inputJson, setInputJson, null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    initializeTags()
  }, [])

  const handleChange = (e: any) => {
    const name = e.target.name
    const value = e.target.value

    if (name === 'intentDescription') {
      const newInputJson = [...inputJson]
      newInputJson[intentIndex] = {
        ...intent,
        intentDescription: value
      }
      setInputJson(newInputJson)
    }

    if (name === 'tag') {
      const newInputJson = [...inputJson]
      const currentTag = intent?.tag || ''

      if (!isTagAvailable(value, currentTag)) {
        toast.error('This tag name is already in use. Please choose another name.')
        e.target.value = currentTag
        return
      }

      // Remove old tag from session if it exists
      if (currentTag && currentTag.trim() !== '') {
        removeTagFromSession(currentTag)
      }

      // Add new tag to session
      if (value && value.trim() !== '') {
        const tagAdded = addTagToSession(value)
        if (tagAdded) {
          newInputJson[intentIndex] = {
            ...intent,
            tag: value.trim()
          }
          setInputJson(newInputJson)
        }
      } else {
        newInputJson[intentIndex] = {
          ...intent,
          tag: value
        }
        setInputJson(newInputJson)
      }
    }

    if (name === 'header') {
      const newInputJson = [...inputJson]
      newInputJson[intentIndex] = {
        ...intent,
        interactive: {
          ...intent.interactive,
          header: {
            ...intent.interactive.header,
            text: value
          }
        }
      }
      setInputJson(newInputJson)
    }

    if (name === 'body') {
      if (value.length > 1024) {
        toast.error('Body text cannot exceed 1024 characters')
        return
      }
      const newInputJson = [...inputJson]
      newInputJson[intentIndex] = {
        ...intent,
        interactive: {
          ...intent.interactive,
          body: {
            ...intent.interactive.body,
            text: value
          }
        }
      }
      setInputJson(newInputJson)
    }

    if (name === 'footer') {
      const newInputJson = [...inputJson]
      newInputJson[intentIndex] = {
        ...intent,
        interactive: {
          ...intent.interactive,
          footer: {
            ...intent.interactive.footer,
            text: value
          }
        }
      }
      setInputJson(newInputJson)
    }

    if (name.startsWith('btnTitle')) {
      const btnId = parseInt(name.split('-')[2])

      if (value.length > 20) {
        toast.error('Button title cannot exceed 20 characters')
        return
      }

      if (/[*_~]/.test(value)) {
        toast.error('Formatting characters (*, _, ~) are not allowed in button titles')
        return
      }

      const newInputJson = [...inputJson]
      const newButtons = [...newInputJson[intentIndex].interactive.action.buttons]
      newButtons[btnId] = {
        ...newButtons[btnId],
        reply: {
          ...newButtons[btnId].reply,
          title: value
        }
      }
      newInputJson[intentIndex] = {
        ...intent,
        interactive: {
          ...intent.interactive,
          action: {
            ...intent.interactive.action,
            buttons: newButtons
          }
        }
      }
      setInputJson(newInputJson)
    }

    if (!!name.startsWith('btnTrigger') && !!parseInt(value)) {
      const newInputJson = [...inputJson]
      const btnId = parseInt(name.split('-')[2])
      const newButtons = [...newInputJson[intentIndex].interactive.action.buttons]
      newButtons[btnId] = {
        ...newButtons[btnId],
        reply: {
          ...newButtons[btnId].reply,
          trigger: parseInt(value)
        }
      }
      newInputJson[intentIndex] = {
        ...intent,
        interactive: {
          ...intent.interactive,
          action: {
            ...intent.interactive.action,
            buttons: newButtons
          }
        }
      }
      setInputJson(newInputJson)
    }

    if (name === 'trigger' && !!parseInt(value)) {
      const newInputJson = [...inputJson]
      newInputJson[intentIndex] = {
        ...intent,
        trigger: parseInt(value)
      }
      setInputJson(newInputJson)
    }
  }

  const addButton = (btnId: number) => {
    const newInputJson = [...inputJson]

    const newButtons = [...newInputJson[intentIndex].interactive.action.buttons]

    // Check if already at max button limit (3 buttons)
    if (newButtons.length >= 3) {
      toast.warning('WhatsApp interactive messages can have a maximum of 3 buttons')
      return
    }

    const newBtnElement = {
      type: 'reply',
      reply: {
        id: (newButtons.length + 1).toString(),
        trigger: parseInt(inputJson[0].id),
        title: 'New Button'
      }
    }

    newButtons.splice(btnId + 1, 0, newBtnElement)

    newInputJson[intentIndex] = {
      ...intent,
      interactive: {
        ...intent.interactive,
        action: {
          ...intent.interactive.action,
          buttons: newButtons
        }
      }
    }

    setInputJson(newInputJson)
  }

  const deleteButton = (btnId: number) => {
    const newInputJson = [...inputJson]

    const newButtons = [...newInputJson[intentIndex].interactive.action.buttons]

    newButtons.splice(btnId, 1)

    newInputJson[intentIndex] = {
      ...intent,
      interactive: {
        ...intent.interactive,
        action: {
          ...intent.interactive.action,
          buttons: newButtons
        }
      }
    }

    setInputJson(newInputJson)
  }

  const handleSave = async () => {
    const token = localStorage.getItem('auth-token')
    const whatsAppIDString = data?.whatsAppIDString
    const previousIntentId = getValidPreviousIntentId(
      intentIndex,
      inputJson,
      data?.WhatsAppInput || []
    )
    setIsSaving(true)
    await updateWhatsAppIntent({
      token,
      whatsAppIDString,
      intent,
      intentJson: inputJson[intentIndex],
      previousIntentId,
      onSuccess: closeModal
    })
  }

  useEffect(() => {
    console.log({ inputJson })
    console.log({ intent })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputJson])

  let cardTitle

  if (intent.start) {
    cardTitle = 'Welcome Intent'
  } else if (intent.end) {
    cardTitle = 'End Intent'
  } else {
    cardTitle = 'Buttons'
  }

  return (
    <>
      <h5 className='card-title h4 mb-5'>{cardTitle}</h5>
      <>
        <InputGroup className='my-3'>
          <InputGroup.Text>Intent Description</InputGroup.Text>
          <Form.Control
            id={`message-${intent.id}`}
            type='text'
            name='intentDescription'
            defaultValue={intent?.intentDescription}
            onBlur={handleChange}
          />
        </InputGroup>

        <InputGroup className='my-3'>
          <InputGroup.Text>Tag</InputGroup.Text>
          <Form.Control
            id={`name-${intent.id}`}
            type='text'
            name='tag'
            defaultValue={intent?.tag}
            onBlur={handleChange}
          />
        </InputGroup>

        {intent.interactive?.header && (
          <InputGroup className='my-3'>
            <InputGroup.Text>Header (optional)</InputGroup.Text>
            <Form.Control
              id={`header-${intent.id}`}
              type='text'
              name='header'
              defaultValue={intent.interactive.header.text}
              onBlur={handleChange}
            />
          </InputGroup>
        )}
        {intent.interactive?.body && (
          <>
            <p className='mb-0 mt-2'>Text (max. 1024 characters)</p>
            <InputGroup className='mb-3'>
              <InputGroup.Text>Text</InputGroup.Text>
              <Form.Control
                id={`body-${intent.id}`}
                as='textarea'
                name='body'
                defaultValue={intent.interactive.body.text}
                maxLength={1024}
                onBlur={handleChange}
              />
            </InputGroup>
          </>
        )}

        {intent.interactive?.footer && (
          <InputGroup className='my-3'>
            <InputGroup.Text>Footer (optional)</InputGroup.Text>
            <Form.Control
              id={`footer-${intent.id}`}
              type='text'
              name='footer'
              defaultValue={intent.interactive.footer.text}
              onBlur={handleChange}
            />
          </InputGroup>
        )}

        {intent.interactive?.action?.buttons && intent.interactive.action.buttons.length > 0 && (
          <div className='pb-7'>
            <h3 className='fs-5 fw-bolder px-3 py-2 mt-3'>
              Buttons <small className='text-muted'>(Max 3)</small>
            </h3>
            {intent.interactive.action.buttons.map((btn: any, index: number) => (
              <Form
                className='my-1'
                onSubmit={(e) => e.preventDefault()}
                key={uniqueId()}
                id={`btn-${intent.id}-${index}`}
              >
                <Row>
                  <Col xs={7}>
                    <FloatingLabel controlId='floatingInput' label='Title'>
                      <Form.Control
                        id={`btnTitle-${intent.id}-${index}`}
                        name={`btnTitle-${intent.id}-${index}`}
                        defaultValue={btn.reply.title}
                        maxLength={20}
                        onBlur={handleChange}
                      />
                    </FloatingLabel>
                  </Col>
                  <Col xs={3}>
                    <FloatingLabel label='Trigger'>
                      <Form.Select
                        id={`btnTrigger-${intent.id}-${index}`}
                        name={`btnTrigger-${intent.id}-${index}`}
                        aria-label='Select Trigger'
                        defaultValue={btn.reply.trigger}
                        onChange={handleChange}
                      >
                        <TriggerDescription inputJson={inputJson} />
                      </Form.Select>
                    </FloatingLabel>
                  </Col>
                  <Col xs={2} ref={btnMenuDropdownRef}>
                    <Dropdown drop='down-centered'>
                      <Dropdown.Toggle size='sm' variant='secondary' id='dropdown-basic' />
                      <Dropdown.Menu className='border border-secondary'>
                        <Dropdown.Item
                          onClick={() => addButton(index)}
                          disabled={intent.interactive.action.buttons.length >= 3}
                        >
                          <i className='bi bi-plus fs-3 p-0 m-0 text-primary' />
                          <span className='ms-2'>Add New</span>
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => deleteButton(index)}
                          disabled={intent.interactive.action.buttons.length <= 1}
                        >
                          <i className='bi bi-trash fs-3 p-0 m-0 text-danger' />
                          <span className='ms-2'>Delete</span>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </Col>
                </Row>
              </Form>
            ))}
          </div>
        )}

        {!!intent.trigger && (
          <InputGroup className='my-3'>
            <InputGroup.Text>Trigger</InputGroup.Text>
            <Form.Select
              id={`trigger-${intent.id}`}
              name='trigger'
              aria-label='Select Trigger'
              defaultValue={intent.trigger}
              onChange={handleChange}
            >
              {allIntentIdsArray &&
                allIntentIdsArray.length > 0 &&
                allIntentIdsArray.map((intentId) => (
                  <option key={intentId} value={intentId}>
                    {intentId}
                  </option>
                ))}
            </Form.Select>
          </InputGroup>
        )}

        <div className='d-flex'>
          <label className='me-3 fs-5 fw-bolder mt-3'>
            Webhook
            <div className='form-check form-switch mt-3'>
              <input
                className='form-check-input'
                type='checkbox'
                id='flexSwitchCheckDefault'
                role='switch'
                name='webhookComponents'
                checked={isWebhookChecked}
                onChange={handleWebhookCheckboxChange}
              />
            </div>
          </label>
        </div>
        {/* )} */}

        {isWebhookChecked && (
          <WhatsappWebhookComponent
            inputJson={inputJson}
            intent={intent}
            handleFulfillmentChange={handleFulfillmentChange}
          />
        )}

        <div className='alert alert-warning mt-4' role='alert'>
          <strong>Buttons Restrictions:</strong>
          <ul className='mb-0'>
            <li>
              🔘 <strong>Buttons</strong>:
              <ul>
                <li>
                  Maximum of <strong>3 buttons</strong> per message
                </li>
                <li>
                  Button title: <strong>max 20 characters</strong>
                </li>
                <li>
                  No formatting allowed (e.g., <code>*bold*</code>, <code>_italic_</code>,{' '}
                  <code>~strikethrough~</code>)
                </li>
                <li>Emojis are allowed</li>
              </ul>
            </li>
            <li>
              📝 <strong>Body Text</strong>:
              <ul>
                <li>
                  Maximum of <strong>1024 characters</strong>
                </li>
                <li>Text will be truncated if the limit is exceeded</li>
              </ul>
            </li>
          </ul>
        </div>

        <div className='d-flex ms-auto mt-auto'>
          <button className='btn btn-secondary me-4' onClick={closeModal}>
            <i className='bi bi-eye-fill' /> Preview
          </button>
          <button className='btn btn-primary' onClick={handleSave} disabled={isSaving}>
            <i className='bi bi-save' /> {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </>
    </>
  )
}

export default ButtonGroup
